import { usePresentationMode } from "../contexts/PresentationModeContext";
import { cn } from "../components/ui/utils";
import type { PresentationMode } from "../utils/presentation";

export type CardSceneLayout = {
  isCard: boolean;
  mainClassName: string;
  titleClassName: string;
  subtitleClassName: string;
  hideDecorativeIcon: boolean;
  mediaClassName: string;
  listClassName: string;
  itemClassName: string;
};

const CLASSIC_LAYOUT: CardSceneLayout = {
  isCard: false,
  mainClassName: "",
  titleClassName: "",
  subtitleClassName: "",
  hideDecorativeIcon: false,
  mediaClassName: "",
  listClassName: "",
  itemClassName: "",
};

const CARDS_LAYOUT: CardSceneLayout = {
  isCard: true,
  mainClassName: "h-auto min-h-full max-w-none mx-0 items-center justify-start gap-0 px-0 sm:px-0 lg:px-0 py-0 overflow-y-visible",
  titleClassName:
    "w-full max-w-full whitespace-normal break-words text-balance text-[1.55rem] leading-tight sm:text-[1.85rem] md:text-[1.85rem] px-1.5 shrink-0",
  subtitleClassName: "mt-1.5 mb-4 max-w-xl mx-auto text-[1.05rem] leading-normal shrink-0",
  hideDecorativeIcon: true,
  mediaClassName: "w-full max-h-[22rem] shrink-0",
  listClassName: "w-full max-w-none space-y-3",
  itemClassName: "p-4 sm:p-[1.15rem]",
};

const LAYOUT_BY_MODE: Record<PresentationMode, CardSceneLayout> = {
  classic: CLASSIC_LAYOUT,
  cards: CARDS_LAYOUT,
};

/** Portrait phone stage/frame for smishing + vishing in card mode. */
export const CARD_PHONE_STAGE_CLASS =
  "flex min-h-0 w-full flex-1 items-center justify-center";
export const CARD_PHONE_FRAME_CLASS =
  "relative h-full max-h-full w-auto aspect-[9/19] max-w-[min(20rem,100%)] overflow-hidden";

export function cardSceneLayout(mode: PresentationMode): CardSceneLayout {
  return LAYOUT_BY_MODE[mode];
}

export function useCardSceneLayout(): CardSceneLayout {
  return cardSceneLayout(usePresentationMode());
}

export function sceneMainClassName(
  base: string,
  layout: CardSceneLayout,
  extra?: string
): string {
  return cn(base, layout.mainClassName, extra);
}

export function sceneTitleClassName(layout: CardSceneLayout, extra?: string): string {
  return cn("project-title", layout.titleClassName, extra);
}

export function sceneSubtitleClassName(layout: CardSceneLayout, extra?: string): string {
  return cn("project-subtitle", layout.subtitleClassName, extra);
}

export function sceneListClassName(
  base: string,
  layout: CardSceneLayout,
  extra?: string
): string {
  return cn(base, layout.listClassName, extra);
}

export function sceneItemClassName(
  base: string,
  layout: CardSceneLayout,
  extra?: string
): string {
  return cn(base, layout.itemClassName, extra);
}

export function sceneMediaClassName(
  base: string,
  layout: CardSceneLayout,
  extra?: string
): string {
  return cn(base, layout.mediaClassName, extra);
}
